import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import {
  ForecastData,
  ForecastDay,
  ManagerSetupState,
  PrepItem,
  PrepSubItem,
  SchedulePrepData,
  UptData
} from 'src/types/apps/managerSetup'

dayjs.extend(isoWeek)

const DEFAULT_AM_PERCENT = 0

/** Monday (ISO) for the selected week offset — shared by save / hydrate. */
export function prepWeekMonday(weekOffset = 0): dayjs.Dayjs {
  return dayjs()
    .startOf('isoWeek')
    .add(Math.max(0, weekOffset) * 7, 'day')
}

// How many weeks ahead we allow (until end of current month)
export function maxWeekOffset(): number {
  const today = dayjs()
  const endOfMonth = today.endOf('month')
  let offset = 0
  while (true) {
    const nextMonday = prepWeekMonday(offset + 1)
    if (nextMonday.isAfter(endOfMonth)) break
    offset++
  }

  return offset
}

/** Empty Mon–Sun skeleton — Tab 1 values come from Fabric via BE (no fake seed). */
function buildEmptyWeekDays(weekOffset = 0): ForecastDay[] {
  const monday = prepWeekMonday(weekOffset)

  return Array.from({ length: 7 }, (_, i) => {
    const date = monday.add(i, 'day')

    return {
      date: date.format('YYYY-MM-DD'),
      day: date.format('dddd D MMM'),
      ctForecast: 0,
      amPercent: DEFAULT_AM_PERCENT
    }
  })
}

/** Loose match for schedule hydrate (legacy slug keys vs section-N / display names). */
function scheduleKeyMatch(id: string, label: string, key?: string | null): boolean {
  if (!key) return false
  if (id === key || label === key) return true
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '')
  const nk = norm(key)
  const ni = norm(id)
  const nl = norm(label)
  if (!nk) return false
  if (ni === nk || nl === nk) return true

  // "prep-in-advance" ↔ "3. Prep in Advance"
  return nl.includes(nk) || nk.includes(nl)
}

function applySavedScheduleFields(
  target: { amPrepTime?: string; pmPrepTime?: string; combinePrep?: boolean; combinePrepTime?: string },
  saved: {
    amPrepTime?: string | null
    pmPrepTime?: string | null
    combinePrep?: boolean
    combinePrepTime?: string | null
  }
) {
  target.amPrepTime = saved.amPrepTime ?? undefined
  target.pmPrepTime = saved.pmPrepTime ?? undefined
  target.combinePrep = Boolean(saved.combinePrep)
  target.combinePrepTime = saved.combinePrepTime ?? undefined
}

/** Bulk-sync combine flag + time on first checkbox only. Later row edits stay local. */
function syncCombinePrepGroup(item: PrepItem, combinePrep: boolean, combinePrepTime?: string) {
  item.combinePrep = combinePrep
  item.subItems?.forEach(sub => {
    sub.combinePrep = combinePrep
  })

  if (!combinePrep) return

  const time = combinePrepTime || item.combinePrepTime || item.amPrepTime || item.pmPrepTime
  if (!time) return

  item.combinePrepTime = time
  item.subItems?.forEach(sub => {
    sub.combinePrepTime = time
  })
}

/** After hydrate: turn combine on for the group; fill blank child times, never overwrite saved ones. */
function syncHydratedCombineGroups(schedule: SchedulePrepData) {
  schedule.sections.forEach(section => {
    section.items.forEach(item => {
      const isCombineGroup = Boolean(item.hasCombinePrep || item.subItems?.some(sub => sub.hasCombinePrep))
      if (!isCombineGroup || !item.subItems?.length) return

      const groupCombined = item.combinePrep || item.subItems.some(sub => sub.combinePrep)
      if (!groupCombined) return

      item.combinePrep = true
      if (!item.combinePrepTime) {
        const fromChild = item.subItems.find(sub => sub.combinePrepTime)
        if (fromChild?.combinePrepTime) item.combinePrepTime = fromChild.combinePrepTime
      }

      item.subItems.forEach(sub => {
        sub.combinePrep = true
        if (!sub.combinePrepTime && item.combinePrepTime) {
          sub.combinePrepTime = item.combinePrepTime
        }
      })
    })
  })
}

// Normalize API response (dayDate/dayLabel) → frontend shape (date/day)
function normalizeForecastDays(days: any[]): ForecastDay[] {
  return days.map(d => ({
    date: d.date ?? d.dayDate?.substring(0, 10) ?? '',
    day: d.day ?? d.dayLabel ?? '',
    ctForecast: d.ctForecast ?? 0,
    managerOverride: d.managerOverride ?? undefined,

    // Whole percent — avoid long decimals like 29.22222
    amPercent: Math.round(Number(d.amPercent ?? DEFAULT_AM_PERCENT))
  }))
}

// ─── Reference data ──────────────────────────────────────────────────────────
// Tab 1 forecast + Tab 2 current-week UPT from Fabric via BE. Tab 3 from config + seed.

export const fetchReferenceData = createAsyncThunk(
  'managerSetup/fetchReferenceData',
  async ({ restaurantId, weekOffset = 0 }: { restaurantId: string; weekOffset?: number }, { extra }: any) => {
    const monday = prepWeekMonday(weekOffset)
    const weekOf = monday.format('D MMM')
    const ctSyncedAt = `Today, ${dayjs().format('h:mm A')}`

    try {
      const { data } = await extra.httpClient.get(`/preplists/restaurant/${restaurantId}/manager/reference-data`, {
        params: { weekOffset }
      })

      // ResponseTransformInterceptor wraps as { data: payload }
      const payload = data?.data ?? data
      if (!payload?.forecastData) {
        throw new Error('reference-data missing forecastData')
      }

      // Normalize API field names (dayDate/dayLabel → date/day)
      return {
        ...payload,
        forecastData: {
          ...payload.forecastData,
          days: normalizeForecastDays(payload.forecastData?.days ?? [])
        }
      } as {
        weekOf: string
        ctSyncedAt: string
        forecastData: ForecastData
        uptData: UptData
        schedulePrepData: SchedulePrepData
      }
    } catch {
      // Offline / API failure — empty shells only (no hardcoded products).
      return {
        weekOf,
        ctSyncedAt,
        forecastData: { weekOf, ctSyncedAt, days: buildEmptyWeekDays(weekOffset) } as ForecastData,
        uptData: {
          weekOf,
          ctSyncedAt,
          sections: []
        } as UptData,
        schedulePrepData: {
          weekOf,
          ctSyncedAt,
          sections: [
            { name: 'Cook Prep', items: [] },
            { name: 'Line Prep', items: [] }
          ]
        } as SchedulePrepData
      }
    }
  }
)

// ─── Local draft helpers ──────────────────────────────────────────────────────
const lsKey = (restaurantId: string) => `manager-prep-draft-${restaurantId}`

function saveDraftLocally(restaurantId: string, payload: any) {
  try {
    localStorage.setItem(lsKey(restaurantId), JSON.stringify({ ...payload, savedAt: new Date().toISOString() }))
  } catch {
    /* storage quota */
  }
}

function loadDraftLocally(restaurantId: string) {
  try {
    const raw = localStorage.getItem(lsKey(restaurantId))

    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// ─── Load existing draft for current week (if any) ───────────────────────────

export const fetchCurrentWeekSubmission = createAsyncThunk(
  'managerSetup/fetchCurrentWeekSubmission',
  async ({ restaurantId, weekOffset = 0 }: { restaurantId: string; weekOffset?: number }, { extra }: any) => {
    const monday = prepWeekMonday(weekOffset).format('YYYY-MM-DD')

    try {
      const { data } = await extra.httpClient.get(
        `/preplists/restaurant/${restaurantId}/manager/submissions/current-week`,
        {
          params: { weekOffset }
        }
      )
      const submission = data?.data ?? data
      if (submission?.weekStartDate || submission?.scheduleItems || submission?.forecastDays) {
        return submission
      }
    } catch {
      /* fall through to local draft */
    }

    const local = loadDraftLocally(restaurantId)
    if (local?.weekStartDate === monday) return local

    return null
  }
)

// ─── Save (draft or submit) ───────────────────────────────────────────────────

export const saveManagerPrep = createAsyncThunk(
  'managerSetup/saveManagerPrep',
  async (
    {
      restaurantId,
      payload
    }: {
      restaurantId: string
      payload: {
        weekStartDate: string
        status?: 'draft' | 'submitted'
        forecastDays: any[]
        uptItems: any[]
        scheduleItems: any[]
      }
    },
    { extra, rejectWithValue }: any
  ) => {
    // Always persist locally so data survives page refresh / offline
    saveDraftLocally(restaurantId, payload)

    try {
      const { data } = await extra.httpClient.post(`/preplists/restaurant/${restaurantId}/manager/submissions`, payload)
      const saved = data?.data ?? data
      if (!saved) {
        return rejectWithValue('Save succeeded but no submission was returned')
      }

      return saved
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to save prep to server (kept locally)'

      return rejectWithValue(Array.isArray(message) ? message.join(', ') : message)
    }
  }
)

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: ManagerSetupState = {
  activeStep: 0,
  weekOffset: 0,
  forecastData: null,
  uptData: null,
  schedulePrepData: null,
  loading: false,
  saving: false,
  errors: null,
  savedSteps: [false, false, false]
}

const managerSetupSlice = createSlice({
  name: 'managerSetup',
  initialState,
  reducers: {
    setActiveStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload
    },
    setWeekOffset(state, action: PayloadAction<number>) {
      state.weekOffset = action.payload

      // Show existing TabSkeleton while reference-data refetches for the new week
      state.loading = true
    },
    updateForecastDay(
      state,
      action: PayloadAction<{ index: number; field: 'managerOverride' | 'amPercent'; value: number | undefined }>
    ) {
      if (!state.forecastData) return
      const { index, field, value } = action.payload
      state.forecastData.days[index][field] = value as number
    },
    updateUptOverride(state, action: PayloadAction<{ sectionIndex: number; itemIndex: number; value?: number }>) {
      if (!state.uptData) return
      const { sectionIndex, itemIndex, value } = action.payload
      state.uptData.sections[sectionIndex].items[itemIndex].override = value
    },
    updatePrepItem(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        field: keyof PrepItem
        value: string | boolean | undefined
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, field, value } = action.payload
      ;(state.schedulePrepData.sections[sectionIndex].items[itemIndex] as any)[field] = value
    },
    applyCombinePrepToGroup(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        combinePrep: boolean
        combinePrepTime?: string
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, combinePrep, combinePrepTime } = action.payload
      const item = state.schedulePrepData.sections[sectionIndex].items[itemIndex]
      syncCombinePrepGroup(item, combinePrep, combinePrepTime)
    },
    updatePrepSubItem(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        subItemIndex: number
        field: keyof PrepSubItem
        value: string | boolean | undefined
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, subItemIndex, field, value } = action.payload
      const subItems = state.schedulePrepData.sections[sectionIndex].items[itemIndex].subItems
      if (subItems) (subItems[subItemIndex] as any)[field] = value
    },
    applyPrepTimeToSubItems(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        field: 'amPrepTime' | 'pmPrepTime'
        value: string
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, field, value } = action.payload
      const item = state.schedulePrepData.sections[sectionIndex].items[itemIndex]
      item[field] = value
      item.subItems?.forEach(sub => {
        sub[field] = value
      })
    },
    markStepSaved(state, action: PayloadAction<number>) {
      state.savedSteps[action.payload] = true
    },
    resetManagerSetup(state) {
      state.activeStep = 0
      state.weekOffset = 0
      state.savedSteps = [false, false, false]
      state.errors = null
      state.forecastData = null
      state.uptData = null
      state.schedulePrepData = null

      // Show TabSkeleton immediately until fetchReferenceData settles
      state.loading = true
    }
  },
  extraReducers: builder => {
    // fetchReferenceData
    builder
      .addCase(fetchReferenceData.pending, state => {
        state.loading = true
      })
      .addCase(fetchReferenceData.fulfilled, (state, action) => {
        state.loading = false
        state.forecastData = action.payload.forecastData
        state.uptData = action.payload.uptData
        state.schedulePrepData = action.payload.schedulePrepData
      })
      .addCase(fetchReferenceData.rejected, (state, action) => {
        state.loading = false
        state.errors = action.error
      })

    // fetchCurrentWeekSubmission — hydrate overrides only; do not toggle page loading
    // (avoids TabSkeleton flash after forecast already rendered)
    builder
      .addCase(fetchCurrentWeekSubmission.fulfilled, (state, action) => {
        const submission = action.payload
        if (!submission) return

        // Hydrate manager overrides and AM% from saved draft
        if (state.forecastData && submission.forecastDays?.length) {
          submission.forecastDays.forEach((saved: any) => {
            const idx = state.forecastData!.days.findIndex(d => d.date === saved.dayDate?.substring(0, 10))
            if (idx >= 0) {
              state.forecastData!.days[idx].managerOverride = saved.managerOverride
              state.forecastData!.days[idx].amPercent = saved.amPercent
            }
          })
        }

        if (state.uptData && submission.uptItems?.length) {
          submission.uptItems.forEach((saved: any) => {
            state.uptData!.sections.forEach(section => {
              if (section.name === saved.sectionName) {
                const item = section.items.find(i => i.id === saved.itemKey)
                if (item) item.override = saved.managerOverride
              }
            })
          })
        }

        if (state.schedulePrepData && submission.scheduleItems?.length) {
          submission.scheduleItems.forEach((saved: any) => {
            state.schedulePrepData!.sections.forEach(section => {
              if (section.name !== saved.sectionName) return

              if (saved.parentItemKey) {
                const parent =
                  section.items.find(i => scheduleKeyMatch(i.id, i.item, saved.parentItemKey)) ??
                  section.items.find(i =>
                    i.subItems?.some(
                      s =>
                        scheduleKeyMatch(s.id, s.item, saved.itemKey) || scheduleKeyMatch(s.id, s.item, saved.itemLabel)
                    )
                  )
                const sub = parent?.subItems?.find(
                  s => scheduleKeyMatch(s.id, s.item, saved.itemKey) || scheduleKeyMatch(s.id, s.item, saved.itemLabel)
                )
                if (sub) applySavedScheduleFields(sub, saved)

                return
              }

              const item = section.items.find(
                i => scheduleKeyMatch(i.id, i.item, saved.itemKey) || scheduleKeyMatch(i.id, i.item, saved.itemLabel)
              )
              if (item) applySavedScheduleFields(item, saved)
            })
          })
          syncHydratedCombineGroups(state.schedulePrepData)
        }
      })
      .addCase(fetchCurrentWeekSubmission.rejected, (state, action) => {
        state.loading = false
        state.errors = action.error
      })

    // saveManagerPrep
    builder
      .addCase(saveManagerPrep.pending, state => {
        state.saving = true
      })
      .addCase(saveManagerPrep.fulfilled, state => {
        state.saving = false
      })
      .addCase(saveManagerPrep.rejected, (state, action) => {
        state.saving = false
        state.errors = action.error
      })
  }
})

export const {
  setActiveStep,
  setWeekOffset,
  updateForecastDay,
  updateUptOverride,
  updatePrepItem,
  updatePrepSubItem,
  applyPrepTimeToSubItems,
  applyCombinePrepToGroup,
  markStepSaved,
  resetManagerSetup
} = managerSetupSlice.actions

export default managerSetupSlice.reducer
